import React, { useState, useEffect } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import toastr from 'shared/helpers/toastr';
import Modal from 'shared/modals/Modal';
import LoadingSpinner from 'shared/loading/LoadingSpinner';
import Table from 'shared/tables/Table';
import FormikDatePicker from 'shared/forms/FormikDatePicker';
import { FormikSelect, FormikTextarea, FormikToggle } from 'shared/forms';
import errorToastr from 'shared/helpers/errorToastr';
// helpers
import { dateFormatter } from 'shared/../constants/Formatting';
// fetch
import { getOrderReschedule, postOrderReschedule, getRescheduleFaultType } from 'src/fetch/orders';

const RescheduleModal = ({
  isOpen,
  close,
  proNumber,
  values,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  isSubmitting,
  isValid,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // static lists
  const [faultTypes, setFaultTypes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [faultTypesResponse, dataResponse] = await Promise.all([getRescheduleFaultType(), getOrderReschedule(proNumber)]);
      setData(dataResponse);
      setFaultTypes(faultTypesResponse);
      setLoading(false);
    } catch (loadError) {
      toastr('error', `Could not load order rescheduling for order ${proNumber}`);
      setLoading(false);
      console.log(loadError);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // table select functionality
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(0);

  const toggleRow = legId => {
    const newSelected = [...selected];
    const currentIndex = newSelected.indexOf(legId);

    if (currentIndex === -1) {
      newSelected.push(legId);

      const selectedLeg = data.find(d => d.orderLegId === legId);
      if (selectedLeg) {
        const selectedApptDate = selectedLeg.appointmentStartDate;

        data.forEach(d => {
          if ((d.appointmentStartDate === selectedApptDate) && (d.orderLegId !== legId)) {
            newSelected.push(d.orderLegId);
          }
        });
      }

    } else {
      newSelected.splice(currentIndex, 1);
    }

    setFieldValue('orderLegIds', newSelected);
    setSelected(newSelected);
    setSelectAll(newSelected.length > 0 ? 2 : 0);
  };

  const toggleSelectAll = () => {
    let newSelected = [];

    if (selectAll === 0) {
      data.forEach(x => {
        newSelected.push(x.orderLegId);
      });
    }

    setFieldValue('orderLegIds', newSelected);
    setSelected(newSelected);
    setSelectAll(newSelected.length > 0 ? 2 : 0);
  };

  return (
    <Modal isOpen={isOpen} handleClose={close} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='order-profit-modal'>
          Order Rescheduling
        </h5>
        <button className='button large close' onClick={close} />
      </div>
      <section className='modal-body'>
        <div>Select legs to reschedule</div>
        {loading ? <LoadingSpinner /> : (
          <React.Fragment>
            <Table
              className='table--primary-color'
              data={data}
              hasSelects
              columns={[
                {
                  id: 'checkbox',
                  accessor: '',
                  Cell: ({ original }) => {
                    return (
                      <input
                        type='checkbox'
                        className='checkbox select-checkbox-table'
                        checked={selected.indexOf(original.orderLegId) > -1}
                        onChange={() => toggleRow(original.orderLegId)}
                      />
                    );
                  },
                  Header: () => {
                    return (
                      <input
                        type='checkbox'
                        className='checkbox select-checkbox-table'
                        checked={selectAll === 1}
                        ref={input => {
                          if (input) {
                            input.indeterminate = selectAll === 2;
                          }
                        }}
                        onChange={toggleSelectAll}
                      />
                    );
                  },
                  sortable: false,
                  resizable: false,
                  width: 35
                },
                { Header: 'Leg #', accessor: 'legNumber', width: 50 },
                { Header: 'Origin', accessor: 'origin' },
                { Header: 'Origin Appt Date', accessor: 'appointmentStartDate', Cell: ({ original }) => dateFormatter(original.appointmentStartDate), width: 120 },
                { Header: 'Destination', accessor: 'destination' },
                { Header: 'Dest Appt Date', accessor: 'appointmentEndDate', Cell: ({ original }) => dateFormatter(original.appointmentEndDate), width: 120 },
                { Header: 'Driver', accessor: 'driver' }
              ]}
            />
            <form onSubmit={handleSubmit}>
              <div className='formik-form'>
                <div className='formik-column'>
                  <div className='formik-row'>
                    <FormikSelect
                      onChange={selected => setFieldValue('sendToScheduler', selected.value)}
                      wrapperClass='wrapper-three-column'
                      label='Reschedule Type'
                      options={data.some(d => d.sendToSchedulerEnabled) ?
                        [{ label: 'Reschedule Now', value: false }, { label: 'Send to Scheduler', value: true }] :
                        [{ label: 'Reschedule Now', value: false }]
                      }
                      name='sendToScheduler'
                      value={values.sendToScheduler}
                      onBlur={setFieldTouched}
                      touched={touched}
                      errors={errors}
                      disabled={!selected.length}
                    />
                    <FormikSelect
                      onChange={selected => setFieldValue('orderRescheduleFaultType', selected.value)}
                      wrapperClass='wrapper-three-column'
                      label='Fault Type'
                      options={faultTypes.map(x => ({ label: x.description, value: x.value }))}
                      name='orderRescheduleFaultType'
                      value={values.orderRescheduleFaultType}
                      onBlur={setFieldTouched}
                      touched={touched}
                      errors={errors}
                      disabled={!selected.length}
                    />
                    {!values.sendToScheduler && (
                      <FormikToggle
                        wrapperClass='wrapper-three-column'
                        label='Require New Dispatch'
                        name='requireNewDispatch'
                        value={values.requireNewDispatch}
                        onToggle={value => setFieldValue('requireNewDispatch', value)}
                        touched={touched}
                        onTouch={() => setFieldTouched('requireNewDispatch', true)}
                        errors={errors}
                        onText='Yes'
                        offText='No'
                        disabled={!selected.length}
                      />
                    )}
                  </div>
                  <div className='formik-row'>
                    {!values.sendToScheduler && (
                      <div className='formik-column'>
                        <div className='formik-row'>
                          <FormikDatePicker
                            wrapperClass='wrapper-three-column'
                            label='New Origin Appointment Date'
                            name='rescheduleModel.originAppointmentDate'
                            value={values.rescheduleModel.originAppointmentDate}
                            onChange={val => {
                              setFieldValue('rescheduleModel.originAppointmentDate', val);
                              setFieldValue('rescheduleModel.destinationAppointmentDate', val);
                            }}
                            onBlur={e => handleBlur(e)}
                            errors={touched && touched.rescheduleModel && touched.rescheduleModel.originAppointmentDate ? errors : {}}
                            setTouched={name => setFieldTouched(name, true)}
                            disabled={!selected.length}
                          />
                          <FormikDatePicker
                            wrapperClass='wrapper-three-column'
                            label='New Destination Appointment Date'
                            name='rescheduleModel.destinationAppointmentDate'
                            value={values.rescheduleModel.destinationAppointmentDate}
                            onChange={val => setFieldValue('rescheduleModel.destinationAppointmentDate', val)}
                            onBlur={e => handleBlur(e)}
                            errors={touched && touched.rescheduleModel && touched.rescheduleModel.destinationAppointmentDate ? errors : {}}
                            setTouched={name => setFieldTouched(name, true)}
                            pickerOptions={{ minDate: values.rescheduleModel.originAppointmentDate }}
                            disabled={!selected.length}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='formik-row'>
                    <FormikTextarea
                      wrapperClass='wrapper-fill-column wrapper-text-area-input'
                      type='text'
                      label='Comments'
                      name='rescheduleModel.comments'
                      value={values.rescheduleModel.comments}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={!selected.length}
                    />
                  </div>
                </div>
              </div>
            </form>
          </React.Fragment>
        )}
      </section>
      <section className='modal-footer toolbar toolbar--xxs'>
        <div className='toolbar--column toolbar--right'>
          <button
            type='button'
            disabled={!isValid || isSubmitting}
            className='button large primary text-align--center toolbar--button--margin'
            onClick={handleSubmit}
          >
            Submit
          </button>
          <button type='button' className='button large white text-align--center toolbar--button--margin' onClick={close}>
            Cancel
          </button>
        </div>
      </section>
    </Modal>
  );
};

const RescheduleSchema = Yup.object().shape({
  sendToScheduler: Yup.boolean().required('Reschedule type is required').typeError('Reschedule type is required'),
  orderRescheduleFaultType: Yup.number().required('Fault type is required').typeError('Fault type is required'),
  orderLegIds: Yup.array(),
  requireNewDispatch: Yup.boolean().nullable(),
  rescheduleModel: Yup.object().shape({
    originAppointmentDate: Yup.string().when('sendToScheduler', {
      is: sendToScheduler => sendToScheduler === false,
      then: Yup.string().required('Origin appointment date is required').typeError('Origin appointment date is required'),
      otherwise: Yup.string().nullable(),
    }),
    destinationAppointmentDate: Yup.string().when('sendToScheduler', {
      is: sendToScheduler => sendToScheduler === false,
      then: Yup.string().required('Destination appointment date is required').typeError('Destination appointment date is required'),
      otherwise: Yup.string().nullable(),
    }),
    comments: Yup.string().required('Comment is required'),
  })
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {} }) => {
    return {
      sendToScheduler: false,
      requireNewDispatch: false,
      orderRescheduleFaultType: 2,
      rescheduleModel: {
        originAppointmentDate: '',
        destinationAppointmentDate: '',
        comments: ''
      },
      orderLegIds: [],
      ...initialValues
    };
  },
  validationSchema: RescheduleSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close, refresh } }) => {
    setSubmitting(true);

    console.log('values', values);
    try {
      await postOrderReschedule({ ...values });
      close(true);
      setSubmitting(false);
      resetForm();

      toastr('success', 'Reschedule Saved Successfully');
      refresh();
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Reschedule Failed'
      });
      setSubmitting(false);
    }
  }
})(RescheduleModal);